import { IEnv } from '../../../infrastructure/i-env';
import { ICurrentUser } from '../../../infrastructure/i-current-user';
import { CategoryDto } from '../../../dto/categories';
import { NewsTitle } from '../../../dto/news-titles';
import { Language } from '../../../dto/language';
import { getNewsSourceLanguage, getNewsTargetLanguage } from '../../../dto/user-preferences-dto';
import { TranslationMode } from '../../../dto/translation-mode';
import { JobRef, JobStatus, ProgressItem } from '../../../dto/sync';
import { NewsCategoryStore } from '../../../stores/news-category-store';
import { NewsTitlesStore } from '../../../stores/news-titles-store';
import { ISyncApiService } from '../../../services/i-sync-api-service';
import { delay } from '../../../utils/delay';

export interface NewsListState {
	categories: CategoryDto[];
	currentCategory: CategoryDto | null;
	articles: NewsTitle[];
	isLoadingMore: boolean;
	hasMoreArticles: boolean;
	showInitialSpinner: boolean;

	currentMode: TranslationMode;
	currentNativeLanguage: Language;
	currentLearnedLanguage: Language;

	progressItems: ProgressItem[];
	currentOperation?: string;
	showProgressBanner: boolean;

	transliterate: boolean;

	isPolling: boolean;
	isSyncing: boolean;
	syncCount: number;
	lastSyncError: string;
}

/**
 * Abstract view model for news list page.
 */
export abstract class INewsListViewModel {
	constructor(
			protected readonly env: IEnv,
			protected readonly currentUser: ICurrentUser
	) {
	}

	abstract readonly state: NewsListState;

	abstract loadCategories(nativeLanguage: Language, learnedLanguage: Language, mode: TranslationMode): Promise<void>;

	abstract loadArticles(
			nativeLanguage: Language,
			learnedLanguage: Language,
			categoryId: string,
			mode: TranslationMode,
			isLoadMore?: boolean
	): Promise<void>;

	abstract loadMoreArticles(nativeLanguage: Language, learnedLanguage: Language, categoryId: string, mode: TranslationMode): Promise<void>;

	abstract openArticle(articleId: string, nativeLanguage: Language, learnedLanguage: Language, categoryId: string): void;

	abstract navigateToWordCollection(nativeLanguage: Language, learnedLanguage: Language): Promise<void>;

	abstract navigateToRepeat(nativeLanguage: Language, learnedLanguage: Language): Promise<void>;

	abstract setCurrentCategoryById(categoryId: string): void;

	abstract toggleTransliterate(): void;

	abstract init(nativeLanguage: Language, learnedLanguage: Language, categoryId: string | null, mode: TranslationMode): Promise<void>;

	abstract dispose(): void;
}

/**
 * Framework-agnostic news list view model.
 * Manages news list state and logic.
 */
export class NewsListViewModel extends INewsListViewModel {
	public readonly state: NewsListState;
	private categoryStore: NewsCategoryStore | null = null;
	private titlesStore: NewsTitlesStore | null = null;
	private readonly pollIntervalMs: number = 2000;
	private currentJobs: JobRef[] = [];
	private pollIntervalId: ReturnType<typeof setInterval> | null = null;
	private lastProgressSignature: string = '';

	constructor(
			env: IEnv,
			currentUser: ICurrentUser,
			private readonly syncApiService: ISyncApiService
	) {
		super(env, currentUser);

		this.state = env.reactive({
			syncCount: 0,
			categories: [],
			currentCategory: null,
			articles: [],
			isLoadingMore: false,
			hasMoreArticles: true,
			showInitialSpinner: true,
			currentMode: TranslationMode.Partial,
			currentNativeLanguage: Language.EN,
			currentLearnedLanguage: Language.EN,
			progressItems: [],
			showProgressBanner: false,
			shouldTranslate: false,
			transliterate: false,
			isPolling: false,
			isSyncing: false,
			lastSyncError: ''
		}) as NewsListState;

		this.recomputeDerived();
	}

	private async ensureStores(): Promise<void> {
		if (this.categoryStore && this.titlesStore) return;
		this.categoryStore ??= this.env.get(NewsCategoryStore);
		this.titlesStore ??= this.env.get(NewsTitlesStore);
	}

	private resolveNewsLanguages(nativeLanguage: Language, learnedLanguage: Language, mode: TranslationMode): { sourceLang: Language; targetLang: Language } {
		return {
			sourceLang: getNewsSourceLanguage({ nativeLanguage, learnedLanguage, mode }),
			targetLang: getNewsTargetLanguage({ nativeLanguage, learnedLanguage, mode })
		};
	}

	async loadCategories(nativeLanguage: Language, learnedLanguage: Language, mode: TranslationMode): Promise<void> {
		try {
			this.state.currentNativeLanguage = nativeLanguage;
			this.state.currentLearnedLanguage = learnedLanguage;
			this.state.currentMode = mode;
			this.recomputeDerived();

			await this.ensureStores();
			const categoryStore = this.categoryStore!;

			let userId: string | undefined;
			try {
				await this.currentUser.initialize();
				userId = this.currentUser.getUserId() ?? undefined;
			} catch {
				userId = undefined;
			}

			const { sourceLang, targetLang } = this.resolveNewsLanguages(nativeLanguage, learnedLanguage, mode);
			const response = await categoryStore.getNewsCategories(sourceLang, targetLang, mode, userId);
			this.state.categories = [
				...response.categories,
				...(response.userCategories ?? [])
			];
			this.recomputeDerived();

			if (this.state.categories.length > 0 && !this.state.currentCategory) {
				this.state.currentCategory = this.state.categories[0];
			}
		} catch (error) {
			console.error('Error loading categories:', error);
		}
	}

	async loadArticles(
			nativeLanguage: Language,
			learnedLanguage: Language,
			categoryId: string,
			mode: TranslationMode,
			isLoadMore = false
	): Promise<void> {
		if (!this.state.currentCategory) return;

		// Stop polling when switching to a different category (not when loading more)
		if (!isLoadMore) {
			this.stopPolling();
			this.currentJobs = [];
			this.state.progressItems = [];
			this.lastProgressSignature = '';
			this.state.isSyncing = false;
			this.recomputeDerived();
		}

		try {
			const { sourceLang, targetLang } = this.resolveNewsLanguages(nativeLanguage, learnedLanguage, mode);
			if (isLoadMore) {
				this.state.isLoadingMore = true;
				await this.ensureStores();
				const titlesStore = this.titlesStore!;
				const response = await titlesStore.loadMoreTitles(sourceLang, targetLang, categoryId, mode);
				this.state.hasMoreArticles = response.hasMore;
			} else {
				const result = await this.fetchLatestArticles(nativeLanguage, learnedLanguage, categoryId, mode);
				if (result.isNewsOutdated) {
					this.startSyncIfNeeded(sourceLang, targetLang, categoryId).then();
				}
			}
		} catch (error) {
			console.error('Error loading articles:', error);
		} finally {
			this.state.isLoadingMore = false;
			this.recomputeDerived();
		}
	}

	private async fetchLatestArticles(nativeLanguage: Language, learnedLanguage: Language, categoryId: string, mode: TranslationMode): Promise<{
		isNewsOutdated: boolean
	}> {
		if (!this.state.currentCategory) return { isNewsOutdated: false };

		try {
			this.state.currentNativeLanguage = nativeLanguage;
			this.state.currentLearnedLanguage = learnedLanguage;
			this.state.currentMode = mode;
			this.recomputeDerived();

			await this.ensureStores();
			const titlesStore = this.titlesStore!;
			const { sourceLang, targetLang } = this.resolveNewsLanguages(nativeLanguage, learnedLanguage, mode);

			const cachedItems = titlesStore.getCachedTitles(sourceLang, targetLang, categoryId, mode);
			if (cachedItems) {
				this.state.articles = cachedItems;
				this.state.showInitialSpinner = false;
				this.recomputeDerived();
				return { isNewsOutdated: false };
			}

			this.state.showInitialSpinner = true;
			const response = await titlesStore.fetchLatestArticles(sourceLang, targetLang, categoryId, mode);

			this.state.articles = response.items;
			this.state.hasMoreArticles = response.hasMore;
			this.recomputeDerived();

			return { isNewsOutdated: response.isNewsOutdated ?? false };
		} catch (error) {
			console.error('Error fetching latest articles:', error);
			return { isNewsOutdated: false };
		} finally {
			this.state.showInitialSpinner = false;
			this.recomputeDerived();
		}
	}

	private async startSyncIfNeeded(sourceLang: Language, targetLang: Language, categoryId: string): Promise<void> {
		if (this.state.isSyncing) return;

		// Stop any existing polling before starting new sync
		this.stopPolling();

		try {
			this.state.isSyncing = true;
			this.state.lastSyncError = '';
			this.recomputeDerived();

			let userId: string | undefined;
			try {
				await this.currentUser.initialize();
				userId = this.currentUser.getUserId() ?? undefined;
			} catch {
				userId = undefined;
			}

			// Step 1: Sync news (import only)
			const importResponse = await this.syncApiService.syncNews({
				userId,
				categoryId,
				sourceLanguage: sourceLang
			});

			this.state.progressItems = importResponse.progressItems;
			this.currentJobs = importResponse.progressItems.map(p => p.jobRef);
			this.recomputeDerived();

			// Start polling for import jobs
			this.startPolling(sourceLang, targetLang, categoryId);

			// Wait for import jobs to complete
			await this.waitForCurrentJobsCompletion();
			this.stopPolling();

			// Step 2: Translate news
			const translateResponse = await this.syncApiService.translateNews({
				userId,
				categoryId,
				sourceLanguage: sourceLang,
				targetLanguage: targetLang,
				mode: this.state.currentMode
			});

			this.state.progressItems = translateResponse.progressItems;
			this.currentJobs = translateResponse.progressItems.map(p => p.jobRef);
			this.recomputeDerived();
			this.startPolling(sourceLang, targetLang, categoryId);
			await this.waitForCurrentJobsCompletion();
			this.stopPolling();
			await this.refreshTitlesDuringSync(sourceLang, targetLang, categoryId);


		} catch (error) {
			const message = error instanceof Error ? error.message:'Unknown error starting sync';
			this.state.lastSyncError = message;
			this.state.progressItems = [];
			this.recomputeDerived();
			console.error('Error starting sync:', error);
		} finally {
			this.state.isSyncing = false;
			this.currentJobs = [];
			this.lastProgressSignature = '';
			this.state.progressItems = [];
			this.state.currentOperation = undefined;
			this.stopPolling();
			this.state.syncCount++;
		}
	}

	public async waitSyncFinished(): Promise<void> {
		while (this.state.isSyncing || this.state.syncCount==0)
			await delay(10);
	}

	public async waitForCurrentJobsCompletion(): Promise<void> {
		return new Promise((resolve) => {
			const checkCompletion = () => {
				if (this.currentJobs.length===0) {
					resolve();
					return;
				}

				const allDone = this.state.progressItems.every(item =>
						item.status===JobStatus.Completed ||
						item.status===JobStatus.Failed ||
						item.status===JobStatus.MaxRetriesExceeded
				);

				if (allDone) {
					resolve();
				} else {
					// Check again in 1 second
					setTimeout(checkCompletion, 100);
				}
			};

			checkCompletion();
		});
	}

	private startPolling(sourceLang: Language, targetLang: Language, categoryId: string): void {
		if (this.state.isPolling || this.currentJobs.length===0) return;

		this.state.isPolling = true;
		this.recomputeDerived();
		this.pollIntervalId = setInterval(async () => {
			await this.fetchProgress(sourceLang, targetLang, categoryId);
		}, this.pollIntervalMs);
	}

	private stopPolling(): void {
		if (this.pollIntervalId) {
			clearInterval(this.pollIntervalId);
			this.pollIntervalId = null;
		}
		this.state.isPolling = false;
		this.recomputeDerived();
	}

	private async fetchProgress(sourceLang: Language, targetLang: Language, categoryId: string): Promise<void> {
		if (this.currentJobs.length===0)
			return;

		try {
			const prevSig = this.lastProgressSignature;
			const progressItems = await this.syncApiService.getSyncStatus(this.currentJobs);
			this.state.progressItems = progressItems;
			this.state.currentOperation = this.getCurrentOperation(progressItems);
			this.recomputeDerived();

			const nextSig = this.computeProgressSignature(progressItems);
			if (nextSig!==prevSig) {
				this.lastProgressSignature = nextSig;
				// Show newly translated titles ASAP while syncing (prepend latest items at the top).
				await this.refreshTitlesDuringSync(sourceLang, targetLang, categoryId);
			}
		} catch (error) {
			const message = error instanceof Error ? error.message:'Unknown error polling sync progress';
			this.state.lastSyncError = message;
			console.error('Error fetching sync progress:', error);
			this.stopPolling();
			this.state.isSyncing = false;
			this.currentJobs = [];
			this.state.progressItems = [];
			this.lastProgressSignature = '';
			this.recomputeDerived();
		}
	}

	private computeProgressSignature(items: readonly ProgressItem[]): string {
		return items
				.map(p => `${p.jobRef.jobType}:${p.jobRef.jobId}:${p.status}:${p.completed}/${p.total}:${p.percentage}`)
				.sort()
				.join('|');
	}

	private async refreshTitlesDuringSync(sourceLang: Language, targetLang: Language, categoryId: string): Promise<void> {
		try {
			await this.ensureStores();
			const titlesStore = this.titlesStore!;

			titlesStore.expireCache(sourceLang, targetLang, categoryId, this.state.currentMode);
			await titlesStore.fetchLatestArticles(sourceLang, targetLang, categoryId, this.state.currentMode);

			// Update UI state with the updated cache
			const cached = titlesStore.getCachedTitles(sourceLang, targetLang, categoryId, this.state.currentMode);
			if (cached) {
				this.state.articles = cached;
				this.recomputeDerived();
			}
		} catch {
			// Best effort only: if refresh fails we keep current list.
		}
	}

	async loadMoreArticles(nativeLanguage: Language, learnedLanguage: Language, categoryId: string, mode: TranslationMode): Promise<void> {
		await this.loadArticles(nativeLanguage, learnedLanguage, categoryId, mode, true);
	}

	openArticle(articleId: string, nativeLanguage: Language, learnedLanguage: Language, categoryId: string): void {
		void this.env.routing.navigate(this.env.routing.getArticleLink(nativeLanguage, learnedLanguage, this.state.currentMode, categoryId, articleId));
	}

	async navigateToWordCollection(nativeLanguage: Language, learnedLanguage: Language): Promise<void> {
		if (!(await this.ensureAuthenticated())) {
			return;
		}

		await this.env.routing.navigate(this.env.routing.getWordCollectionLink(nativeLanguage, learnedLanguage));
	}

	async navigateToRepeat(nativeLanguage: Language, learnedLanguage: Language): Promise<void> {
		if (!(await this.ensureAuthenticated())) {
			return;
		}

		await this.env.routing.navigate(this.env.routing.getWordRepeatLink(nativeLanguage, learnedLanguage));
	}

	getCurrentOperation(items: ProgressItem[]): string | undefined {
		for (let item of items) {
			if (item.status===JobStatus.Running && item.currentOperation)
				return this.env.localizedStringToText(item.currentOperation);
		}
		if (items.find(x => x.status===JobStatus.Pending))
			return 'pending';
		if (items.find(x => x.status===JobStatus.Running))
			return 'running';
		if (items.find(x => x.status===JobStatus.Completed))
			return 'complete';
		return 'analyzing';
	}

	setCurrentCategoryById(categoryId: string): void {
		if (this.state.categories.length > 0) {
			const matchedCategory = this.state.categories.find(cat => cat.id===categoryId);
			if (matchedCategory && matchedCategory!==this.state.currentCategory) {
				this.state.currentCategory = matchedCategory;
				this.recomputeDerived();
			}
		}
	}

	async init(nativeLanguage: Language, learnedLanguage: Language, categoryId: string | null, mode: TranslationMode): Promise<void> {
		await this.loadCategories(nativeLanguage, learnedLanguage, mode);

		if (categoryId && this.state.categories.length > 0) {
			this.setCurrentCategoryById(categoryId);
		}

		if (this.state.currentCategory) {
			await this.loadArticles(nativeLanguage, learnedLanguage, this.state.currentCategory.id, mode);
		}
	}

	toggleTransliterate(): void {
		this.state.transliterate = !this.state.transliterate;
		this.recomputeDerived();
	}

	private async ensureAuthenticated(): Promise<boolean> {
		await this.currentUser.initialize();
		if (!this.currentUser.getUserId()) {
			await this.env.routing.navigate(this.env.routing.getLoginLink());
			return false;
		}
		return true;
	}

	private recomputeDerived(): void {
		this.state.showProgressBanner = this.state.progressItems.length > 0;
	}

	/**
	 * Debug-friendly string representation of the current view-model state.
	 * Intended for tests and troubleshooting.
	 */
	public format(): string {
		const currentCategoryId = this.state.currentCategory?.id ?? '(none)';
		const categoryIds = this.state.categories.map(c => c.id);
		const categoriesLine = categoryIds.length > 0
				? categoryIds.map(id => (id===currentCategoryId ? `*${id}`:id)).join(', ')
				:'(none)';

		const progressLines = [...this.state.progressItems]
				.map(p => {
					const op = (() => {
						const v = p.currentOperation;
						if (!v) return '';
						if (v.stringId==='common.rawText') {
							const textParam = v.formatParams?.text;
							if (typeof textParam==='string' || typeof textParam==='number') {
								return String(textParam);
							}
						}
						return this.env.localizedStringToText(v);
					})();
					return `${p.jobRef.jobType}: ${op} ${p.completed}/${p.total} (${p.percentage}%)`;
				})
				.join('\n');

		const titleLines = this.state.articles
				.slice(0, 10)
				.map(a => `- ${a.title.source} (${a.date})`)
				.join('\n');

		return [
			`categories: ${categoriesLine}`,
			`syncing: ${this.state.isSyncing} polling: ${this.state.isPolling}`,
			`progress:`,
			progressLines || '(none)',
			`titles (first ${Math.min(10, this.state.articles.length)}/${this.state.articles.length}):`,
			titleLines || '(none)'
		].join('\n');
	}

	/** Alias for tests that prefer `print()` naming. */
	public print(): string {
		return this.format();
	}

	/**
	 * Test helper: force a single progress refresh without relying on timers.
	 * Safe no-op if not currently syncing/polling or no jobs are tracked.
	 */
	public async updateProgress(): Promise<void> {
		const categoryId = this.state.currentCategory?.id;
		if (!categoryId) return;
		const { sourceLang, targetLang } = this.resolveNewsLanguages(
				this.state.currentNativeLanguage,
				this.state.currentLearnedLanguage,
				this.state.currentMode
		);
		await this.fetchProgress(sourceLang, targetLang, categoryId);
	}

	/** Test helper alias. */
	public getSnapshot(): string {
		return this.format();
	}

	public dispose(): void {
		this.stopPolling();
		this.currentJobs = [];
		this.state.progressItems = [];
		this.state.isSyncing = false;
	}
}
